import { Tooltip } from '@mui/material';
import { usePermission } from 'hooks/usePermission';
import React from 'react';
import { useIntl } from 'react-intl';
import { Permission, PermissionScope } from 'types/dto';
import { Permissions } from 'utils/permission';

export interface IHasOptions {
  has: boolean;
  permission: Permissions;
}

type hasOptionsRenderer = (options: IHasOptions) => React.ReactElement<any, any>;

export const Has: React.FC<{
  children: unknown;
  permission?: Permission;
  all?: Permission[];
  any?: Permission[];
  tooltip?: string | boolean;
  scope?: PermissionScope | null;
}> = ({ children, permission: requestedPermission, all, any, tooltip, scope }) => {
  const { formatMessage } = useIntl();
  const permission = usePermission();
  let has = false;
  let protectedChildren: React.ReactElement<any, any> | null;

  if (all) {
    has = permission?.hasAllOf(all, scope) || false;
  } else if (any) {
    has = permission?.hasOneOf(any, scope) || false;
  } else if (requestedPermission) {
    has = permission?.has(requestedPermission, scope) || false;
  } else {
    throw new Error("please specify one of this props 'permission', 'all', 'any'");
  }

  protectedChildren =
    typeof children === 'function' ? (
      (children as hasOptionsRenderer)({ has, permission: permission! })
    ) : has ? (
      <>{children}</>
    ) : null;

  if (!has && tooltip) {
    const title =
      typeof tooltip === 'boolean'
        ? formatMessage({ id: 'common.noAccess', defaultMessage: 'You have no access' })
        : tooltip;
    return (
      <Tooltip title={title} placement={'top'}>
        <div>{protectedChildren!}</div>
      </Tooltip>
    );
  }

  return protectedChildren;
};
